/**
 * Main App Component - Refactored for better maintainability
 *
 * This refactored version splits the large monolithic component into
 * focused, reusable components while maintaining identical behavior.
 */
import { useMemo, useState, Suspense, lazy } from 'react';
import { FaClock } from 'react-icons/fa';

// Assets
import pokemonBg from './pokemonbg.webp';
import polaroid2 from './polaroid-2.webp';
import polaroidTest from './polaroid-test.webp';

// Configuration and constants
import { BRAND_CONFIG } from './config/brand';
import { GAME_DETAILS } from './consts/games-dynamic';
import { SCHEDULE } from './consts/schedule';

// Custom hooks
import { useMobileDetection } from './hooks/useMobileDetection';
import { useFontLoader } from './hooks/useFontLoader';
import { useImagePreloader } from './hooks/useImagePreloader';
import { useCarousel } from './hooks/useCarousel';
import useGlobalLazyImages from './utils/useGlobalLazyImages';
import usePersistImageSize from './utils/usePersistImageSize';
import usePreloadImage from './utils/usePreloadImage';

// Components
import LoadingSkeleton from './components/LoadingSkeleton';
import Header from './components/Header/Header';
import HeroSection from './components/Hero/HeroSection';
import AboutSection from './components/Sections/AboutSection';

// Lazy loaded components
const GlossyCard = lazy(() => import('./components/GlossyCard'));
const Day = lazy(() => import('./components/Day'));

/**
 * Main Application Component
 */
export default function App() {
  // Game and carousel state
  const games = useMemo(() => Object.values(GAME_DETAILS), []);
  const [hasSelectedGame, setHasSelectedGame] = useState(false);
  const [shineDisabled, setShineDisabled] = useState(false);

  // Stock section state
  const [selectedStock, setSelectedStock] = useState(
    GAME_DETAILS.pokemon.packsInStore.entries().next().value[1],
  );
  const [stockBgColour, setStockBgColour] = useState(
    GAME_DETAILS.pokemon.packsInStore.entries().next().value[1].bg + '15',
  );
  const [tookALook, setTookALook] = useState(false);

  // Custom hooks
  const isMobile = useMobileDetection();
  const fontsReady = useFontLoader(0);
  const { preloadGameImages } = useImagePreloader();
  const {
    currentGameIndex,
    setCurrentGameIndex,
    carouselRef,
    handleScroll,
    fadeToSlide,
  } = useCarousel(games, hasSelectedGame, setHasSelectedGame);

  // Initialize image loading
  usePreloadImage(pokemonBg);
  useGlobalLazyImages();
  usePersistImageSize();

  // Preload game images
  preloadGameImages(games);

  // Show loading skeleton while fonts load
  if (!fontsReady) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="flex min-h-screen w-full max-w-full min-w-full flex-col text-zinc-100">
      <Header isMobile={isMobile} />

      <HeroSection
        games={games}
        currentGameIndex={currentGameIndex}
        setCurrentGameIndex={setCurrentGameIndex}
        carouselRef={carouselRef}
        handleScroll={handleScroll}
        setHasSelectedGame={setHasSelectedGame}
        fadeToSlide={fadeToSlide}
        isMobile={isMobile}
        shineDisabled={shineDisabled}
        setShineDisabled={setShineDisabled}
      />

      <AboutSection />

      {/* What We Offer Section */}
      <section
        id="what-we-offer"
        className="flex border-y-1 border-y-amber-400/20 bg-[#2a211b]/30 py-15"
      >
        <div className="mx-auto flex h-full w-full max-w-[95vw] flex-col justify-center px-4 md:max-w-[80vw]">
          <h1 className="fira-sans-black text-center text-5xl font-bold">
            What we offer
          </h1>

          {/* For Collectors */}
          <div className="mt-10 flex flex-wrap gap-4 md:flex-nowrap">
            <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-2/3 md:pr-10">
              <h2 className="fira-sans-black text-4xl font-bold">
                For <span className="text-blue-300">collectors</span>
              </h2>
              <ul className="list-disc space-y-4 pl-5">
                <li>
                  <strong>Booster packs</strong> — a huge range of boosters,
                  with a constantly changing selection!
                </li>
                <li>
                  <strong>Singles</strong> — come take a look at our infamous
                  Wall of Singles, featuring more cards than you can imagine.
                </li>
                <li>
                  <strong>Vintage & Specials</strong> — thanks to our regular
                  trips to the US, we stock items that you won't find anywhere
                  else, including vintage singles and rare boosters.
                </li>
                <li>
                  <strong>Trade-ins</strong> — turn unwanted cards into cash or
                  trade-in credit! We offer top rates, providing an easy way to
                  maximise your collection's value.
                </li>
              </ul>
            </div>
            <div className="flex w-full flex-shrink-0 flex-col gap-2 pt-8 md:w-1/3 md:pt-0">
              <img
                className="max-h-[300px] -rotate-1 object-contain md:rotate-3"
                src={polaroidTest}
                alt="A selection of TCG stock at Full Fire TCG"
              />
            </div>
          </div>

          {/* For Players */}
          <div className="mt-10 flex flex-wrap gap-4 pt-10 md:flex-nowrap">
            <div className="hidden w-full flex-shrink-0 flex-col gap-2 pr-10 md:flex md:w-1/3">
              <img
                className="max-h-[300px] -rotate-1 object-contain md:-rotate-3"
                src={polaroid2}
                alt="A group of players at Full Fire TCG"
              />
            </div>
            <div className="flex w-full flex-shrink-0 flex-col gap-2 pt-8 md:w-2/3 md:pt-0">
              <h2 className="fira-sans-black text-4xl font-bold">
                For <span className="text-red-300">players</span>
              </h2>
              <ul className="list-disc space-y-4 pl-5">
                <li>
                  <strong>Tournaments & Prizes</strong> — regular tournaments
                  for MTG, Pokémon, and other games, with awesome prizes to be
                  won!
                </li>
                <li>
                  <strong>Community</strong> — over 130 regulars plus kind and
                  knowledgeable staff make for an atmosphere you can't get
                  enough of.
                </li>
                <li>
                  <strong>Snacks & Refreshments</strong> — always welcome for
                  those long gaming sessions!
                </li>
              </ul>
              <h3 className="fira-sans-black mt-4 text-3xl font-bold">
                Schedule
              </h3>
              <div className="mt-4 flex w-full flex-col gap-2">
                {Object.entries(SCHEDULE).map(([day, info]) => (
                  <Suspense
                    key={day}
                    fallback={
                      <div className="h-8 animate-pulse rounded bg-gray-800" />
                    }
                  >
                    <Day dayName={day} dayInfo={info} showGames={true} />
                  </Suspense>
                ))}
              </div>
            </div>
          </div>

          {/* Kids Section */}
          <div className="mt-10 pt-10">
            <h2 className="fira-sans-black text-4xl font-bold">
              Bringing your kids?
            </h2>
            <p className="mt-8">
              Kids are always welcome at Full Fire! We have a collection of
              cards donated by our regulars that we give away to children for
              free, so they can follow in dad's (or mum's!) footsteps and start
              their own collection.
            </p>
          </div>
        </div>
      </section>

      {/* Stock Section */}
      <section
        id="stock"
        className="flex h-auto border-y-1 border-y-zinc-400/20 bg-[#1a1a1a]/30 py-15"
      >
        <div className="mx-auto flex h-full w-full max-w-[95vw] flex-col justify-center px-4 md:max-w-[80vw]">
          <h1 className="fira-sans-black text-center text-5xl font-bold">
            What's new
          </h1>
          <p className="mt-8">
            Check out a sneak peek of our newest stock below!
          </p>
          <p className="mt-8">
            <strong>Not seeing what you're looking for?</strong> We have a huge
            range of stock that's constantly changing, and we couldn't possibly
            fit it all on this page! Come down to the store to see our full
            range.
          </p>

          {/* Stock Selector */}
          <div
            id="stock-selector"
            className="mt-10 flex justify-center gap-0 overflow-x-clip overflow-y-hidden md:flex-nowrap"
          >
            {[...GAME_DETAILS.pokemon.packsInStore.entries()].map(
              ([name, info], i) => (
                <div
                  key={name}
                  aria-selected={i === 0}
                  style={{
                    '--sel': info.bg ?? '#333333',
                    '--hov': info.bg ? info.bg + '80' : '#222',
                  }}
                  className="winky-sans-semibold game-stock-select text-l md:h-1rem relative flex max-h-28 flex-1 basis-0 cursor-pointer items-center justify-center overflow-clip border border-zinc-700 bg-zinc-900 px-2 py-3.5 text-center text-[1em] font-semibold text-white backdrop-blur select-none before:absolute before:inset-0 before:-z-10 before:bg-[var(--sel)] before:pb-10 before:opacity-0 before:transition-opacity before:duration-50 before:ease-in-out hover:border-[var(--sel)] hover:shadow-[0_0_20px_var(--sel),_inset_0px_15px_15px_-5px_var(--hov),_inset_0px_-15px_15px_-5px_var(--hov)] hover:before:bg-[var(--hov)] hover:before:opacity-100 aria-selected:!border-zinc-700 aria-selected:shadow-[0_20px_30px_var(--sel),_inset_0px_15px_15px_-5px_var(--hov),_inset_0px_-15px_15px_-5px_var(--hov)] aria-selected:before:opacity-100 aria-selected:hover:cursor-default aria-selected:hover:!bg-[var(--sel)] md:inline md:text-xl"
                  onClick={(e) => {
                    setSelectedStock(info);
                    setStockBgColour(info.bg + '10');

                    // Clear old selection
                    e.currentTarget.parentElement
                      .querySelectorAll(
                        '.game-stock-select[aria-selected="true"]',
                      )
                      .forEach((div) =>
                        div.setAttribute('aria-selected', 'false'),
                      );

                    // Set new selection
                    e.currentTarget.setAttribute('aria-selected', 'true');
                  }}
                >
                  {isMobile ? i + 1 : name}
                </div>
              ),
            )}
          </div>

          {/* Stock Details */}
          <div
            className="relative isolate flex flex-col overflow-hidden border border-t-0 border-zinc-700/50 bg-zinc-950 p-10 pb-7 backdrop-blur-xs"
            style={{ '--bg-tint': stockBgColour }}
          >
            <div className="flex w-full flex-wrap items-start justify-between gap-4 md:flex-nowrap">
              <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-2/3 md:pr-10">
                <h2 className="fira-sans-black text-5xl font-bold">
                  {selectedStock.name || 'Select stock'}
                </h2>
                <h2 className="fira-sans-black pb-6 text-xl font-bold text-gray-400">
                  {selectedStock.year} • {selectedStock.langs} in stock •{' '}
                  {selectedStock.cardCount}+ cards
                </h2>
                {selectedStock.description}

                <div
                  className={`mt-8 transition-opacity duration-150 ease-in-out ${
                    tookALook ? 'md:hidden md:opacity-0' : 'opacity-100'
                  }`}
                >
                  <strong className="!mr-auto hidden !w-auto bg-gradient-to-r from-[#4ec9f6] via-[#ff6fb5] to-[#ffda79] bg-clip-text text-white/30 md:inline">
                    Hover the card to take a closer look!
                  </strong>
                  <strong className="!mr-auto inline !w-auto bg-gradient-to-r from-[#4ec9f6] via-[#ff6fb5] to-[#ffda79] bg-clip-text text-white/30 md:hidden">
                    Touch the card to take a closer look!
                  </strong>
                </div>
              </div>

              <div className="flex w-full flex-shrink-0 flex-col items-center gap-2 pt-8 pb-14 md:w-1/3 md:pt-0 md:pr-10 md:pb-6">
                <div className="group/card h-[300px] max-w-[200px] object-contain md:h-[300px] md:max-w-[200px] md:rotate-2">
                  <Suspense
                    fallback={
                      <div className="h-[300px] w-[200px] animate-pulse rounded-lg bg-gray-800" />
                    }
                  >
                    <GlossyCard
                      card={{
                        image: selectedStock.card,
                        name: selectedStock.cardName,
                        rarity: 'Secret Art Rare',
                      }}
                      callback={() => setTookALook(true)}
                      isMobile={isMobile}
                    />
                  </Suspense>
                  <label className="z-0 -mx-5 flex rotate-0 flex-col items-center px-0 pt-4 !text-lg font-bold text-zinc-100">
                    {selectedStock.cardName}
                    <br />
                    <label className="!text-xs font-bold text-zinc-500">
                      {selectedStock.rarity}
                    </label>
                  </label>
                </div>
              </div>
            </div>
          </div>
          {/* Coming Soon */}
          <div className="mt-12">
            <h2 className="fira-sans-black text-4xl font-bold">Coming soon</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-zinc-700/50 bg-zinc-950/70 p-6 backdrop-blur-xs">
                <h3 className="fira-sans-black text-2xl font-bold">
                  Pokémon — Inferno X
                </h3>
                <p className="fira-sans-black mt-1 text-gray-400">
                  <FaClock className="mr-2 mb-0.5 inline h-3 w-3 align-[-2px]" />
                  November
                </p>
                <p className="mt-3">
                  A blazing new set with hot pulls such as Mega Charizard X ex.
                </p>
              </div>
              <div className="rounded-lg border border-zinc-700/50 bg-zinc-950/70 p-6 backdrop-blur-xs">
                <h3 className="fira-sans-black text-2xl font-bold">
                  Magic: The Gathering — Avatar: The Last Airbender
                </h3>
                <p className="fira-sans-black mt-1 text-gray-400">
                  <FaClock className="mr-2 mb-0.5 inline h-3 w-3 align-[-2px]" />
                  November
                </p>
                <p className="mt-3">
                  Step into the spirit realm. Featuring your favourite
                  characters, like Aang and Katara.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section
        id="location"
        className="flex h-auto items-center justify-center border-y-1 border-y-amber-600/20 bg-[#1d1715] py-14"
      >
        <div className="mx-auto flex h-full w-full max-w-[95vw] flex-col justify-center px-4 md:max-w-[80vw]">
          <h1 className="fira-sans-black text-center text-5xl font-bold">
            Where we are
          </h1>
          <p className="mt-8">
            We're based on the waterfront of Gloucester Docks, a 2-minute walk
            from car parking, and a 5-minute walk from the city center. Come say
            hello!
          </p>

          <div className="flex flex-wrap gap-4 md:flex-nowrap">
            {/* Address */}
            <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-1/2 md:pr-10">
              <h2 className="fira-sans-black mt-8 text-4xl font-bold">
                Address
              </h2>
              <address className="mt-6 not-italic">
                Biddle & Shipton Warehouse
                <br />
                The Docks
                <br />
                Gloucester
                <br />
                Gloucestershire
                <br />
                GL1 2BY
                <br />
              </address>
            </div>

            {/* Opening Hours */}
            <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-1/2 md:pr-10">
              <h2 className="fira-sans-black mt-8 mb-6 text-4xl font-bold">
                Opening hours
              </h2>
              <div className="mt-4 flex w-full flex-col gap-2">
                {Object.entries(SCHEDULE).map(([day, info]) => (
                  <Suspense
                    key={day}
                    fallback={
                      <div className="h-6 animate-pulse rounded bg-gray-800" />
                    }
                  >
                    <Day dayName={day} dayInfo={info} showHours={true} />
                  </Suspense>
                ))}
              </div>
            </div>
          </div>

          {/* Google Maps */}
          <iframe
            title="Google Maps location of Full Fire TCG"
            loading="lazy"
            className="h-[400px] w-full pt-6"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed/v1/place?key=AIzaSyAvNdQJ70b0q6BtQlHAd_HZGZA64mR-PL0&q=Full+Fire+TCG,+Gloucester,+UK"
          />
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="relative flex h-auto items-center justify-center border-b border-b-amber-400/20 bg-[#2a1b1c] py-14 before:absolute before:!z-0 before:!h-full before:!min-h-full before:w-full before:min-w-full before:bg-[url(./smallfire.webp)] before:bg-cover before:bg-bottom before:bg-no-repeat before:mix-blend-overlay before:!content-['']"
      >
        <div className="relative z-10 mx-auto flex h-full w-full max-w-[95vw] flex-col justify-center px-4 md:max-w-[80vw]">
          <h1 className="fira-sans-black text-center text-5xl font-bold">
            Get in touch!
          </h1>
          <p className="mt-8">
            Questions, comments, or just want to say hi? We'd love to hear from
            you! Reach us via email, phone, or social media.
          </p>

          {/* Contact Links */}
          <div className="mt-8 space-y-3">
            <div className="flex items-center">
              <a
                href="mailto:fullfiretcg@gmail.com"
                className="mr-3 inline-flex items-center justify-center rounded-full bg-zinc-900/80 p-2"
                aria-label="Email us"
              >
                <svg
                  className="h-5 w-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </a>
              <strong>Email:</strong>
              <span className="ml-2">
                <a
                  className="text-blue-300 underline hover:text-blue-200"
                  href="mailto:fullfiretcg@gmail.com"
                >
                  fullfiretcg@gmail.com
                </a>
              </span>
            </div>

            <div className="flex items-center">
              <a
                href="tel:+447455931163"
                className="mr-3 inline-flex items-center justify-center rounded-full bg-zinc-900/80 p-2"
                aria-label="Call us"
              >
                <svg
                  className="h-5 w-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </a>
              <strong>Phone:</strong>
              <span className="ml-2">+44 (0)7455 931163</span>
            </div>

            <div className="flex items-center">
              <a
                href="https://www.instagram.com/fullfiretcg/"
                target="_blank"
                rel="noopener noreferrer"
                className="mr-3 inline-flex items-center justify-center rounded-full bg-zinc-900/80 p-2"
                aria-label="Follow us on Instagram"
              >
                <svg
                  className="h-5 w-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <strong>Instagram:</strong>
              <span className="ml-2">
                <a
                  className="text-blue-300 underline hover:text-blue-200"
                  href="https://www.instagram.com/fullfiretcg/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @fullfiretcg
                </a>
              </span>
            </div>

            <div className="flex items-center">
              <a
                href="https://www.facebook.com/people/FULL-FIRE-TCG/61563387998101/"
                target="_blank"
                rel="noopener noreferrer"
                className="mr-3 inline-flex items-center justify-center rounded-full bg-zinc-900/80 p-2"
                aria-label="Follow us on Facebook"
              >
                <svg
                  className="h-5 w-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <strong>Facebook:</strong>
              <span className="ml-2">
                <a
                  className="text-blue-300 underline hover:text-blue-200"
                  href="https://www.facebook.com/people/FULL-FIRE-TCG/61563387998101/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FULL FIRE TCG
                </a>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pt-20 pb-6">
        <div className="flex items-center justify-center gap-4">
          <span className="fira-sans-black text-4xl font-semibold text-white drop-shadow-[0_0_5px_rgba(255,100,0,0.3)] select-none">
            FULL
          </span>
          {BRAND_CONFIG.animatedEmoji}
          <span className="fira-sans-black text-4xl font-semibold text-white drop-shadow-[0_0_5px_rgba(255,100,0,0.3)] select-none">
            FIRE
          </span>
        </div>
        <div className="text-2xs container mx-auto flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="!text-[12px] text-gray-400">
            © 2024 - {new Date().getFullYear()} FULL FIRE TCG LTD. All rights
            reserved.
          </p>
          <p className="!text-[12px] text-gray-400">
            FULL FIRE TCG LTD is a company registered in England &amp; Wales
            (Company No. 15778335).
            <br />
            <br />
            Email:{' '}
            <a
              href="mailto:fullfiretcg@gmail.com"
              className="hover:text-gray-300"
            >
              fullfiretcg@gmail.com
            </a>
            &nbsp;|&nbsp; Tel: +44 (0)7455 931163
          </p>
        </div>
      </footer>
    </div>
  );
}
